import type { ReactNode } from 'react';

export interface FaqEntry {
  id: string;
  title: string;
  answer: ReactNode;
}

export interface FaqSection {
  id: string;
  title: string;
  items: FaqEntry[];
}

interface BulletItem {
  label?: string;
  body: string;
}

const Bullets = ({ items }: { items: BulletItem[] }) => (
  <ul style={{ margin: '8px 0', paddingLeft: 18 }}>
    {items.map((item, idx) => (
      <li key={idx}>
        {item.label && <strong>{item.label}</strong>}
        {item.label ? ` ${item.body}` : item.body}
      </li>
    ))}
  </ul>
);

const planSelection: FaqSection = {
  id: 'plan-selection',
  title: 'Plan Selection',
  items: [
    {
      id: 'package',
      title: 'Which package should I choose?',
      answer: (
        <>
          <p>
            The right plan depends on how many contacts you’re exporting per
            month and how big your team is. As a rough guide:
          </p>
          <Bullets
            items={[
              {
                label: 'Starter (up to 30K credits/month, up to 3 seats):',
                body: 'solo founders, small sales teams, or anyone testing outbound at limited volume. Roughly 20K–30K verified contacts per month.',
              },
              {
                label: 'Builder (60K to 300K credits/month, up to 5 seats):',
                body: 'scaling sales and marketing teams running consistent outbound or ABM campaigns. Roughly 40K–200K verified contacts per month.',
              },
              {
                label: 'Scale (450K+ credits/month, 10 seats):',
                body: 'high-volume GTM teams and lead-gen agencies prospecting across multiple campaigns or clients. 300K+ verified contacts per month.',
              },
              {
                label: 'Enterprise:',
                body: 'custom volumes, seats, API rate limits, onboarding, and security requirements.',
              },
            ]}
          />
          <p>
            Not sure how many credits you’ll need? Try our credit calculator to
            get a personalized recommendation based on your target market,
            monthly export volume, and use of mobile numbers. If you’re between
            two plans, our team can also walk you through your specific use
            case. Just reach out via chat.
          </p>
        </>
      ),
    },
    {
      id: 'pricing',
      title: 'How does AI Ark pricing work?',
      answer: (
        <>
          <p>
            AI Ark runs on a credit-based monthly subscription, built for
            high-volume agencies and GTM teams. On all plans above 30K credits,
            pricing ranges from just $2 to $4 per 1,000 credits, among the most
            competitive rates in the B2B data space.
          </p>
          <p>Plans are structured by credit volume:</p>
          <Bullets
            items={[
              {
                label: 'Starter:',
                body: 'up to 30K credits/month, up to 3 seats',
              },
              {
                label: 'Builder:',
                body: '60K to 300K credits/month, up to 5 seats',
              },
              { label: 'Scale:', body: '450K+ credits/month, 10 seats' },
              {
                label: 'Enterprise:',
                body: 'custom volumes, seats, API rate limits, and onboarding',
              },
            ]}
          />
          <p>
            Every plan includes full access to AI Lookalike Search, Semantic
            Search, Personality Analysis, and the complete API, no features
            locked behind higher tiers. Scaling up unlocks larger API rate
            limits and additional seats.
          </p>
        </>
      ),
    },
    {
      id: 'credits-consumed',
      title: 'How are credits consumed?',
      answer: (
        <>
          <p>
            Credits are spent only when new data is delivered and you only pay
            once for any given contact, ever.
          </p>
          <Bullets
            items={[
              { label: 'Company export:', body: '0.1 credit (one-time)' },
              {
                label: 'Person profile (without contact info):',
                body: '0.5 credit (one-time)',
              },
              {
                label:
                  'Verified email powered by BounceBan, added to a profile:',
                body: '0.5 credit (one-time)',
              },
              {
                label: 'Verified mobile number:',
                body: '5 credits (one-time)',
              },
              {
                label: 'Re-exporting a contact you already own:',
                body: 'free',
              },
              {
                label: 'Re-verifying an email you already own:',
                body: '0.1 credit (only if you actively request a fresh verification)',
              },
            ]}
          />
          <p>
            A typical person export with a verified email costs 1 credit total
            (0.5 profile + 0.5 email). If no valid email is found, you only pay
            the 0.5 base profile cost.
          </p>
          <p>
            Once a contact is in your account, it’s yours. Pulling them up
            again, re-exporting them, or adding them to new lists never costs
            another credit, even months later, across different searches, or
            after upgrading your plan.
          </p>
        </>
      ),
    },
    {
      id: 'rollover',
      title: 'Do unused credits roll over?',
      answer: (
        <p>
          Yes. Unused credits roll over month to month, up to a maximum balance
          of 2× your monthly plan allowance. For example, on a 100,000-credit
          plan you can accumulate up to 200,000 credits before the cap kicks in.
          This protects you from losing credits during slower months while
          preventing indefinite stockpiling.
        </p>
      ),
    },
    {
      id: 'discounts',
      title: 'Do you offer discounts?',
      answer: (
        <p>
          Yes. You can save 20% by paying annually upfront on any paid plan.
          Annual customers also get all credits allocated on day one (rather
          than monthly drip), which is useful for teams running large one-off
          campaigns or with unpredictable monthly volumes. Enterprise customers
          receive custom volume pricing on request.
        </p>
      ),
    },
  ],
};

const frictionRisk: FaqSection = {
  id: 'friction-risk',
  title: 'Friction & Risk',
  items: [
    {
      id: 'free-trial-charge',
      title: 'Will I be charged at the end of my free trial?',
      answer: (
        <p>
          No. The free plan never converts to a paid plan automatically. There’s
          no credit card on file and no surprise charge. When your monthly free
          credits run out, the platform simply pauses exports until the next
          month’s credits refresh, or until you choose to upgrade. You only ever
          pay if you explicitly select a paid plan and enter payment details.
        </p>
      ),
    },
    {
      id: 'contracts',
      title:
        'How long are your contracts? Can I cancel, upgrade, or downgrade my plan?',
      answer: (
        <>
          <p>
            Standard plans run on monthly or annual cycles, and you can choose
            either at signup:
          </p>
          <Bullets
            items={[
              {
                label: 'Monthly:',
                body: 'cancel anytime, no notice period, no cancellation fees.',
              },
              {
                label: 'Annual:',
                body: '20% discount, billed upfront, with all credits allocated on day one.',
              },
              {
                label: 'Enterprise:',
                body: 'custom annual contracts with dedicated onboarding and custom volumes.',
              },
            ]}
          />
          <p>
            All three plan changes are managed from your billing settings in the
            app:
          </p>
          <Bullets
            items={[
              {
                label: 'Upgrade:',
                body: 'takes effect immediately. You’re charged the full new plan price and receive the full new credit allocation the same day, with your renewal date resetting.',
              },
              {
                label: 'Downgrade:',
                body: 'takes effect at the end of your current billing cycle, so you keep your current allocation until then.',
              },
              {
                label: 'Cancellation:',
                body: 'takes effect at the end of the current billing cycle. No notice period, no fees.',
              },
            ]}
          />
          <p>
            Rolled-over credits from your previous plan are preserved on
            upgrades (up to the 2× cap of your new plan allowance).
          </p>
        </>
      ),
    },
    {
      id: 'out-of-credits',
      title: 'What happens if I run out of credits mid-month?',
      answer: (
        <>
          <p>You have two options:</p>
          <Bullets
            items={[
              {
                label: 'Re-subscribe to your current plan immediately:',
                body: 'this resets the billing cycle to the current day and grants you the full new credit allocation right away.',
              },
              {
                label: 'Request a top-up package:',
                body: 'our Customer Success team can arrange one-off credit top-ups so you can keep going without resetting your billing cycle.',
              },
            ]}
          />
          <p>
            Upgrades work the same way: charged at the full new price, with full
            credits granted immediately and a reset renewal date.
          </p>
        </>
      ),
    },
    {
      id: 'get-started',
      title: 'How quickly can I get started?',
      answer: (
        <>
          <p>
            Sign up takes under a minute (no credit card required for the free
            tier). From there:
          </p>
          <Bullets
            items={[
              {
                label: 'Free account:',
                body: '100 credits/month, ready to use immediately',
              },
              {
                label: 'Paid plans:',
                body: 'instant credit grant after checkout, no waiting period',
              },
              {
                label: 'API access:',
                body: 'API key available immediately in your settings',
              },
            ]}
          />
          <p>
            Most users run their first verified export within 10 minutes of
            signing up.
          </p>
        </>
      ),
    },
  ],
};

const dataQuality: FaqSection = {
  id: 'data-quality',
  title: 'Data Quality',
  items: [
    {
      id: 'bounce-rate',
      title: 'What bounce rate should I expect?',
      answer: (
        <p>
          AI Ark customers typically see bounce rates below 1.5%. Every email is
          verified in real time at export through BounceBan, and invalid,
          unknown, or risky addresses are stripped from the file before
          download. This means you can load exports directly into Smartlead,
          Instantly, Lemlist, or any other outreach tool without paying for a
          separate validation step.
        </p>
      ),
    },
    {
      id: 'email-verification-tool',
      title: 'Do I need a separate email verification tool with AI Ark?',
      answer: (
        <p>
          No. Every email is verified in real time at the moment of export, so
          the file you download contains only valid addresses. You do not need
          NeverBounce, ZeroBounce, or any third-party validation tool on top;
          that’s already included. This eliminates an extra cost and step in
          your workflow that’s typically required when working with most other
          B2B data providers.
        </p>
      ),
    },
    {
      id: 'share-contacts',
      title: 'Do I have to share my own contacts to use AI Ark?',
      answer: (
        <p>
          No. AI Ark is a paid platform. You are never asked to share your
          address book, email inbox, calendar, or LinkedIn login to access the
          platform or earn credits. Data is sourced from public records and
          partner networks, and community contributions are handled completely
          separately from individual user accounts. Your data stays yours.
        </p>
      ),
    },
  ],
};

const fitUseCases: FaqSection = {
  id: 'fit-use-cases',
  title: 'Fit & Use Cases',
  items: [
    {
      id: 'non-technical',
      title: 'Is AI Ark suitable for non-technical users?',
      answer: (
        <p>
          Yes. The platform was designed for sales, marketing, and recruiting
          professionals; no SQL, scripting, or data engineering knowledge
          required. You can build complex searches with point-and-click filters,
          save dynamic lists, and export verified data in seconds. For technical
          teams, we also offer a full REST API and an MCP server compatible with
          Cursor, Claude, and Windsurf.
        </p>
      ),
    },
    {
      id: 'integrations',
      title: 'Does AI Ark integrate with my outreach tools and CRM?',
      answer: (
        <>
          <p>
            Yes. AI Ark exports are designed to plug directly into your existing
            stack:
          </p>
          <Bullets
            items={[
              {
                label: 'Outreach tools:',
                body: 'Smartlead, Instantly, Lemlist, Email Bison, Outreach, Salesloft',
              },
              {
                label: 'CRMs:',
                body: 'HubSpot, Salesforce, Pipedrive, and others (via CSV import or API)',
              },
              {
                label: 'Automation platforms:',
                body: 'Zapier, Make, n8n, Clay',
              },
              {
                label: 'AI workflows:',
                body: 'native MCP server for Cursor, Claude Desktop, and Windsurf',
              },
            ]}
          />
          <p>
            Because every email is verified in real time before export, you can
            launch campaigns immediately without paying for a separate
            validation tool. Native CRM connectors are on the roadmap.
          </p>
        </>
      ),
    },
    {
      id: 'agencies',
      title: 'Can agencies use AI Ark for client work?',
      answer: (
        <p>
          Yes. AI Ark is widely used by lead-gen and GTM agencies. CSV exports
          are delivered as unbranded raw data, so you can pass them directly to
          clients without revealing the data source. Credits are pooled at the
          team level so you can flex usage across multiple clients from a single
          workspace, and lists / saved searches are shared by default to prevent
          duplicate prospecting across your team. A White Label UI dashboard for
          resellers is on the roadmap.
        </p>
      ),
    },
  ],
};

const decisionClosing: FaqSection = {
  id: 'decision-closing',
  title: 'Decision-Closing',
  items: [
    {
      id: 'compare-competitors',
      title: 'How does AI Ark compare to Apollo, ZoomInfo, or Lusha?',
      answer: (
        <>
          <p>
            While we won’t speak for other providers, here’s where AI Ark
            customers consistently report a meaningful difference:
          </p>
          <Bullets
            items={[
              {
                label: 'Data freshness:',
                body: 'our entire database is refreshed every 30 days compared to on-request updates or 6–12 month cycles common at legacy providers.',
              },
              {
                label: 'Real-time email verification powered by BounceBan:',
                body: 'every email is verified at the moment of export, on both the platform and the API, no separate validation tool required.',
              },
              {
                label: 'Catch-all email validation:',
                body: 'AI Ark validates and includes catch-all addresses in real time, while most tools mark them as “risky” and exclude them, meaning you reach 30–50% more decision-makers at large companies.',
              },
              {
                label: 'Volume-friendly pricing:',
                body: 'built for high-volume teams, with rates of just $2 to $4 per 1,000 credits on all plans above 30K credits/month.',
              },
              {
                label: 'AI features included on every plan:',
                body: 'Lookalike Search, Semantic Search, and Personality Analysis are built in, not sold as paid add-ons.',
              },
            ]}
          />
          <p>
            We recommend testing on the free tier (100 credits/month, no credit
            card required) and comparing against your current provider on a live
            search of your target market.
          </p>
        </>
      ),
    },
    {
      id: 'enterprise',
      title: 'Does AI Ark work for large enterprises?',
      answer: (
        <>
          <p>Yes. Enterprise customers run on custom plans that include:</p>
          <Bullets
            items={[
              { body: 'Higher monthly credit volumes (millions of credits)' },
              { body: 'Elevated API rate limits and dedicated API keys' },
              { body: 'Additional seats and admin controls' },
              { body: 'Custom data fields and tailored onboarding' },
              { body: 'Direct access to a Customer Success Manager' },
            ]}
          />
          <p>
            Reach out to our sales team via the contact form to discuss volume
            pricing, security questionnaires, and procurement requirements.
          </p>
        </>
      ),
    },
  ],
};

export const FAQ_SECTIONS: FaqSection[] = [
  planSelection,
  frictionRisk,
  dataQuality,
  fitUseCases,
  decisionClosing,
];

export const FAQ_ALL_TAB_ID = 'all';
